import Big from 'big.js';
import { formatProposalDate, formatGbpAmount, toNumberOrNull } from '@/utils/proposalDocument';
import {
	CLIENT_DOCUMENT_BODY_COLOR,
	CLIENT_DOCUMENT_BLUE,
	CLIENT_DOCUMENT_FONT_FAMILY,
	CLIENT_DOCUMENT_LABEL_COLOR,
	buildClientDocumentFooterText,
	buildClientDocumentPageHeaderText,
} from '@/utils/clientDocumentTheme';

export const QUOTATION_DOCUMENT_TYPE = 'quotation';
export const QUOTATION_VAT_RATE = 0.2;
export const QUOTATION_CURRENCY = 'GBP';
export const DEFAULT_QUOTATION_CLEANING_STYLE = 'Sparkling clean';
export const DEFAULT_QUOTATION_PAYMENT_TERMS =
	'An invoice will be issued upon completion of the work. Payment is due within five working days of the invoice date.';

const DEFAULT_COMPANY = {
	name: 'New Order Clean Ltd',
	address: "Office 2, Bennet's House, 21 Leyton Road, Harpenden, AL5 2HU",
	companyNumber: '13205534',
	phone: '07553 702075',
	email: 'info@neworderclean.co.uk',
	website: 'www.neworderclean.co.uk',
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const clone = (value) => JSON.parse(JSON.stringify(value));

const normalizeText = (value, fallback = '') =>
	value === null || value === undefined ? fallback : String(value);

const normalizeNumberInput = (value, fallback = '') => {
	if (value === null || value === undefined || value === '') return fallback;
	const numeric = toNumberOrNull(value);
	return numeric === null ? fallback : numeric;
};

const moneyBig = (value) => {
	const numeric = toNumberOrNull(value);
	return numeric === null ? new Big(0) : new Big(numeric);
};

const roundMoney = (value) => Number(value.round(2, Big.roundHalfUp).toFixed(2));

const escapeHtml = (value) => {
	if (value === null || value === undefined) return '';
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
};

const buildFooterText = (company = {}) => buildClientDocumentFooterText(company);

const normalizeCompany = (value = {}) => ({
	name: normalizeText(value.name, DEFAULT_COMPANY.name),
	address: normalizeText(value.address, DEFAULT_COMPANY.address),
	companyNumber: normalizeText(value.companyNumber, DEFAULT_COMPANY.companyNumber),
	phone: normalizeText(value.phone, DEFAULT_COMPANY.phone),
	email: normalizeText(value.email, DEFAULT_COMPANY.email),
	website: normalizeText(value.website, DEFAULT_COMPANY.website),
});

const normalizeSchedulePhase = (value = {}, index = 0) => ({
	id: normalizeText(value.id, `phase-${index + 1}`),
	title: normalizeText(value.title, index === 0 ? 'Phase 1' : `Phase ${index + 1}`),
	date: normalizeText(value.date),
	team: normalizeText(value.team),
	time: normalizeText(value.time),
});

const normalizeCostRow = (value = {}, index = 0) => ({
	id: normalizeText(value.id, `cost-${index + 1}`),
	item: normalizeText(value.item),
	amount: normalizeNumberInput(value.amount),
});

export const createQuotationTemplateContent = () => ({
	version: 1,
	documentType: QUOTATION_DOCUMENT_TYPE,
	currency: QUOTATION_CURRENCY,
	company: { ...DEFAULT_COMPANY },
	footerText: buildFooterText(DEFAULT_COMPANY),
	title: 'QUOTATION',
	subtitle: DEFAULT_QUOTATION_CLEANING_STYLE,
	quotationNumber: '',
	clientId: null,
	clientName: '',
	clientAddress: '',
	reference: '',
	quotationDate: todayIso(),
	serviceDetails: {
		serviceId: null,
		serviceType: '',
		propertyId: null,
		property: '',
		siteAddress: '',
		scopeOfWork: '',
	},
	schedule: {
		title: 'Work Schedule',
		phases: [
			{
				id: 'phase-1',
				title: 'Phase 1',
				date: '',
				team: '',
				time: '',
			},
		],
	},
	costBreakdown: {
		title: 'Cost Breakdown',
		vatRate: QUOTATION_VAT_RATE,
		rows: [
			{ id: 'cost-1', item: '', amount: '' },
		],
		additionalTerms: '',
	},
	paymentTerms: {
		title: 'Payment Terms',
		text: DEFAULT_QUOTATION_PAYMENT_TERMS,
	},
	acceptance: {
		title: 'Acceptance',
		text: 'On acceptance, please confirm the working dates and access arrangements before work commences.',
		validityText: 'This quotation is valid for 30 days from the date above.',
	},
});

const splitLegacyPaymentTerms = (value = '') => {
	const text = normalizeText(value);
	const marker = /\bPAYMENT\s+TERMS\b\s*:?\s*/i.exec(text);
	if (!marker) {
		return { acceptanceText: text, paymentTermsText: '' };
	}
	return {
		acceptanceText: text.slice(0, marker.index).trim(),
		paymentTermsText: text.slice(marker.index + marker[0].length).trim(),
	};
};

export const normalizeQuotationContent = (raw = {}) => {
	const template = createQuotationTemplateContent();
	const source = raw && typeof raw === 'object' ? raw : {};
	const company = normalizeCompany(source.company || template.company);
	const serviceDetails = source.serviceDetails || {};
	const schedule = source.schedule || {};
	const costBreakdown = source.costBreakdown || {};
	const acceptance = source.acceptance || {};
	const sourcePaymentTerms = source.paymentTerms;
	const paymentTerms =
		sourcePaymentTerms && typeof sourcePaymentTerms === 'object'
			? sourcePaymentTerms
			: { text: sourcePaymentTerms };
	const legacyAcceptance = splitLegacyPaymentTerms(
		normalizeText(acceptance.text, template.acceptance.text),
	);
	const phases = Array.isArray(schedule.phases) ? schedule.phases : template.schedule.phases;
	const rows = Array.isArray(costBreakdown.rows) ? costBreakdown.rows : template.costBreakdown.rows;

	return {
		...clone(template),
		...source,
		version: 1,
		documentType: QUOTATION_DOCUMENT_TYPE,
		currency: normalizeText(source.currency, QUOTATION_CURRENCY),
		company,
		footerText: normalizeText(source.footerText, buildFooterText(company)),
		title: normalizeText(source.title, template.title),
		subtitle: normalizeText(source.subtitle, template.subtitle),
		quotationNumber: normalizeText(source.quotationNumber, source.reference || ''),
		clientId: source.clientId ?? null,
		clientName: normalizeText(source.clientName),
		clientAddress: normalizeText(source.clientAddress),
		reference: normalizeText(source.reference),
		quotationDate: normalizeText(source.quotationDate, template.quotationDate || todayIso()),
		serviceDetails: {
			serviceId: serviceDetails.serviceId ?? null,
			serviceType: normalizeText(serviceDetails.serviceType),
			propertyId: serviceDetails.propertyId ?? null,
			property: normalizeText(serviceDetails.property),
			siteAddress: normalizeText(serviceDetails.siteAddress),
			scopeOfWork: normalizeText(serviceDetails.scopeOfWork),
		},
		schedule: {
			title: normalizeText(schedule.title, template.schedule.title),
			phases: phases.length ? phases.map(normalizeSchedulePhase) : [normalizeSchedulePhase()],
		},
		costBreakdown: {
			title: normalizeText(costBreakdown.title, template.costBreakdown.title),
			vatRate: toNumberOrNull(costBreakdown.vatRate) ?? QUOTATION_VAT_RATE,
			rows: rows.length ? rows.map(normalizeCostRow) : [normalizeCostRow()],
			additionalTerms: normalizeText(costBreakdown.additionalTerms),
		},
		paymentTerms: {
			title: normalizeText(paymentTerms.title, template.paymentTerms.title),
			text: normalizeText(
				paymentTerms.text,
				legacyAcceptance.paymentTermsText || template.paymentTerms.text,
			),
		},
		acceptance: {
			title: normalizeText(acceptance.title, template.acceptance.title),
			text: legacyAcceptance.acceptanceText,
			validityText: normalizeText(acceptance.validityText, template.acceptance.validityText),
		},
	};
};

export const calculateQuotationTotals = (costBreakdownInput = {}) => {
	const costBreakdown = normalizeQuotationContent({ costBreakdown: costBreakdownInput }).costBreakdown;
	const subtotal = costBreakdown.rows.reduce(
		(total, row) => total.plus(moneyBig(row.amount)),
		new Big(0),
	);
	const vat = subtotal.times(costBreakdown.vatRate);
	const total = subtotal.plus(vat);
	return {
		subtotal: roundMoney(subtotal),
		vat: roundMoney(vat),
		total: roundMoney(total),
		vatRate: costBreakdown.vatRate,
	};
};

export const getQuotationPropertySummary = (contentInput = {}) => {
	const content = normalizeQuotationContent(contentInput);
	return [content.serviceDetails.property, content.serviceDetails.siteAddress]
		.filter(Boolean)
		.join(', ');
};

export const buildQuotationPayload = (contentInput = {}, extra = {}) => {
	const content = normalizeQuotationContent(contentInput);
	const totals = calculateQuotationTotals(content.costBreakdown);
	const { metadata = {}, content: _ignoredContent, ...rest } = extra || {};
	return {
		...rest,
		documentType: QUOTATION_DOCUMENT_TYPE,
		title: content.title || 'Quotation',
		reference: content.quotationNumber || content.reference || '',
		documentDate: content.quotationDate || null,
		client: content.clientId || rest.client || null,
		clientName: content.clientName || '',
		propertySummary: getQuotationPropertySummary(content),
		totalAmount: totals.total,
		content,
		metadata: {
			template: 'quotation-v1',
			...metadata,
		},
	};
};

export const deriveQuotationFilename = (document = {}) => {
	const content = normalizeQuotationContent(document.content || document);
	const reference = content.quotationNumber || content.reference || document.reference || 'quotation';
	const safeReference = String(reference)
		.replace(/[^a-zA-Z0-9._-]+/g, '-')
		.replace(/^-+|-+$/g, '')
		.slice(0, 80);
	return `${safeReference || 'quotation'}.pdf`;
};

const renderInfoRows = (rows = []) =>
	rows
		.map(([label, value, options = {}]) => {
			const className = options.strong ? ' class="strong-value"' : '';
			return `
				<tr>
					<th>${escapeHtml(label)}</th>
					<td${className}>${value || '&nbsp;'}</td>
				</tr>
			`;
		})
		.join('');

const renderCostRows = (rows = []) =>
	rows
		.map((row) => `
			<tr data-quotation-cost-row="item">
				<td>${escapeHtml(row.item)}</td>
				<td class="amount">${row.amount === '' ? '' : escapeHtml(formatGbpAmount(row.amount))}</td>
			</tr>
		`)
		.join('');

const renderScopeLines = (value = '') => {
	const logicalLines = String(value || '')
		.split(/\r\n?|\n/)
		.map((line) => line.trim())
		.filter(Boolean);
	const bulletMarkerPattern = /^[-–—•]\s*/u;
	const isBulletList =
		logicalLines.length > 0 &&
		logicalLines.every((line) => bulletMarkerPattern.test(line));
	const lines = logicalLines.flatMap((line) => {
		const text = isBulletList ? line.replace(bulletMarkerPattern, '').trim() : line;
		if (text.length <= 280) return [{ text, isContinuation: false }];
		const chunks = [];
		let chunk = '';
		text.split(/\s+/).forEach((word) => {
			if (!chunk) {
				chunk = word;
				return;
			}
			if (`${chunk} ${word}`.length <= 280) {
				chunk = `${chunk} ${word}`;
				return;
			}
			chunks.push({ text: chunk, isContinuation: chunks.length > 0 });
			chunk = word;
		});
		if (chunk) chunks.push({ text: chunk, isContinuation: chunks.length > 0 });
		return chunks;
	});
	const normalizedLines = lines.length
		? lines
		: [{ text: '', isContinuation: false }];
	return normalizedLines
		.map(
			({ text, isContinuation }) => {
				const bulletClass = isBulletList
					? ` quotation-scope-line--bullet${isContinuation ? '-continuation' : ''}`
					: '';
				return `<div class="quotation-scope-line${bulletClass}">${
					text ? escapeHtml(text) : '&nbsp;'
				}</div>`;
			},
		)
		.join('');
};

const renderSchedulePhases = (phases = []) =>
	phases
		.map((phase) => {
			const formattedPhaseDate = formatProposalDate(phase.date) || phase.date;
			const rows = [
				...(phase.title
					? [['Phase', escapeHtml(phase.title), { strong: true }]]
					: []),
				...(formattedPhaseDate
					? [['Date of work', escapeHtml(formattedPhaseDate)]]
					: []),
				...(phase.team ? [['Cleaning operatives', escapeHtml(phase.team)]] : []),
				...(phase.time
					? [['Estimated cleaning time', escapeHtml(phase.time)]]
					: []),
			];
			if (!rows.length) return '';
			return `
				<div class="schedule-phase">
					<table class="details-table schedule-details-table">
						<tbody>${renderInfoRows(rows)}</tbody>
					</table>
				</div>
			`;
		})
		.join('');

const renderAdditionalTerms = (value = '') => {
	const text = normalizeText(value).trim();
	if (!text) return '';
	const body = text.replace(/^please\s+note\s*:?\s*/i, '').trim();
	return `<p class="terms"><strong>Please note:</strong>${body ? ` ${escapeHtml(body)}` : ''}</p>`;
};

export const generateQuotationHtml = ({ content: contentInput, logoUrl = '/logo-m.svg' } = {}) => {
	const content = normalizeQuotationContent(contentInput);
	const totals = calculateQuotationTotals(content.costBreakdown);
	const footerText = escapeHtml(content.footerText || buildFooterText(content.company));
	const formattedDate = formatProposalDate(content.quotationDate) || content.quotationDate;
	const vatLabel = `VAT (${Math.round(totals.vatRate * 100)}%)`;

	return `
		<div
			class="pdf-gen-root pdf-gen-invoice-document quotation-pdf-root"
			data-client-document-autopaginate="quotation"
		>
			<div class="quotation-document quotation-pagination-source" aria-hidden="true">
				<div class="quotation-flow-block quotation-cover-block">
					<header class="quotation-topbar">
						<div></div>
						${logoUrl ? `<img class="quotation-logo" src="${escapeHtml(logoUrl)}" alt="">` : ''}
					</header>

					<h1>${escapeHtml(content.title)}</h1>
					<p class="quotation-subtitle">${escapeHtml(content.subtitle)}</p>

					<table class="details-table">
						<tbody>
							${renderInfoRows([
								['Quotation No.', escapeHtml(content.quotationNumber), { strong: true }],
								['Client', escapeHtml(content.clientName), { strong: true }],
								...(content.clientAddress
									? [['', escapeHtml(content.clientAddress)]]
									: []),
								...(content.reference
									? [['Reference', escapeHtml(content.reference)]]
									: []),
								['Date', escapeHtml(formattedDate)],
							])}
						</tbody>
					</table>
				</div>

				<div class="quotation-flow-block quotation-service-block" data-split-scope="true">
					<section>
						<h2>Service Details</h2>
						<table class="details-table">
							<tbody>
								${renderInfoRows([
									['Service Type', escapeHtml(content.serviceDetails.serviceType), { strong: true }],
									['Site Address', escapeHtml(content.serviceDetails.siteAddress)],
									['Property Description', escapeHtml(content.serviceDetails.property)],
								])}
								<tr class="quotation-scope-row">
									<th>Scope of Work</th>
									<td><div class="quotation-scope-lines">${renderScopeLines(content.serviceDetails.scopeOfWork)}</div></td>
								</tr>
							</tbody>
						</table>
					</section>
				</div>

				<div class="quotation-flow-block quotation-schedule-block" data-split-schedule="true">
					<section>
						<h2>${escapeHtml(content.schedule.title)}</h2>
						${renderSchedulePhases(content.schedule.phases)}
					</section>
				</div>

				<div class="quotation-flow-block quotation-commercial-block" data-balance-group="true">
					<div class="quotation-commercial-part quotation-commercial-part--cost" data-split-cost="true">
						<section>
							<h2>${escapeHtml(content.costBreakdown.title)}</h2>
							<table class="cost-table">
								<thead>
									<tr>
										<th>Item</th>
										<th class="amount">Amount</th>
									</tr>
								</thead>
								<tbody>
									${renderCostRows(content.costBreakdown.rows)}
									<tr class="total-row" data-quotation-cost-row="summary">
										<td>Estimated total excluding VAT</td>
										<td class="amount">${escapeHtml(formatGbpAmount(totals.subtotal))}</td>
									</tr>
									<tr data-quotation-cost-row="summary">
										<td>${escapeHtml(vatLabel)}</td>
										<td class="amount">${escapeHtml(formatGbpAmount(totals.vat))}</td>
									</tr>
									<tr class="grand-total-row" data-quotation-cost-row="summary">
										<td>Estimated total including VAT</td>
										<td class="amount">${escapeHtml(formatGbpAmount(totals.total))}</td>
									</tr>
								</tbody>
							</table>
							${renderAdditionalTerms(content.costBreakdown.additionalTerms)}
							<div class="quotation-payment-terms">
								<h2>${escapeHtml(content.paymentTerms.title)}</h2>
								<p>${escapeHtml(content.paymentTerms.text)}</p>
							</div>
						</section>
					</div>

					<div class="quotation-commercial-part quotation-commercial-part--acceptance">
						<section>
							<h2>${escapeHtml(content.acceptance.title)}</h2>
							<p>${escapeHtml(content.acceptance.text)}</p>
							<p class="validity">${escapeHtml(content.acceptance.validityText)}</p>
						</section>
					</div>
				</div>
			</div>
			<template data-quotation-footer-template>${footerText}</template>
			<template data-quotation-header-template>${escapeHtml(buildClientDocumentPageHeaderText(content.quotationNumber || content.reference, content.title))}</template>
		</div>
		<style>
			.quotation-pdf-root {
				width: 100%;
				background: #f3f4f6;
				padding: 0;
				font-family: ${CLIENT_DOCUMENT_FONT_FAMILY};
				color: #3f3f46;
			}

			.quotation-document {
				position: relative;
				width: 8.27in;
				height: 11.68in;
				margin: 0 auto;
				padding: 0.3in 0.74in 1.02in;
				background: #fff;
				box-sizing: border-box;
				font-size: 13px;
				font-weight: 400;
				line-height: 1.38;
				overflow: hidden;
				page-break-after: always;
				break-after: page;
			}

			.quotation-document--last {
				page-break-after: auto;
				break-after: auto;
			}

			.quotation-pagination-source {
				position: absolute;
				top: 0;
				left: -10000px;
				height: auto;
				min-height: 0;
				overflow: visible;
				visibility: hidden;
				pointer-events: none;
				page-break-after: auto;
				break-after: auto;
			}

			.quotation-flow-block {
				display: flow-root;
			}

			.quotation-topbar {
				display: flex;
				justify-content: flex-end;
				min-height: 82px;
			}

			.quotation-logo {
				width: auto;
				height: auto;
				max-width: 96px;
				max-height: 75px;
				object-fit: contain;
			}

			.quotation-document h1 {
				margin: 0 0 6px;
				font-size: 21px;
				font-weight: 700;
				line-height: 1.38;
				letter-spacing: 0;
				color: ${CLIENT_DOCUMENT_BLUE};
			}

			.quotation-subtitle {
				margin: 0 0 18px;
				font-size: 14px;
				font-weight: 600;
				color: ${CLIENT_DOCUMENT_BODY_COLOR};
			}

			.quotation-document section {
				margin-top: 28px;
			}

			.quotation-document h2 {
				margin: 0 0 10px;
				font-size: 16px;
				font-weight: 600;
				line-height: 1.25;
				color: ${CLIENT_DOCUMENT_BLUE};
			}

			.details-table,
			.cost-table {
				width: 100%;
				border-collapse: collapse;
				border-spacing: 0;
				border-top: 0;
				border-bottom: 0;
				table-layout: fixed;
			}

			.details-table th,
			.details-table td,
			.cost-table th,
			.cost-table td {
				border: 0;
				padding: 7px 0 8px;
				vertical-align: top;
			}

			.details-table tr:not(:last-child) > *,
			.cost-table thead > tr > *,
			.cost-table tbody > tr:not(:last-child) > * {
				border-bottom: 1px solid #dedede;
			}

			.details-table th {
				width: 200px;
				padding-right: 14px;
				color: ${CLIENT_DOCUMENT_LABEL_COLOR};
				font-weight: 600;
				text-align: left;
			}

			.details-table td.strong-value {
				font-weight: 600;
			}

			.quotation-service-block .details-table {
				border-bottom: 0;
			}

			.quotation-scope-line + .quotation-scope-line {
				margin-top: 2px;
			}

			.quotation-scope-line--bullet,
			.quotation-scope-line--bullet-continuation {
				position: relative;
				padding-left: 14px;
			}

			.quotation-scope-line--bullet::before {
				position: absolute;
				left: 0;
				content: "•";
			}

			.cost-table th {
				color: ${CLIENT_DOCUMENT_BODY_COLOR};
				font-weight: 600;
				text-align: left;
			}

			.cost-table th:first-child,
			.cost-table td:first-child {
				padding-right: 14px;
			}

			.cost-table .amount {
				width: 120px;
				padding-left: 14px;
				text-align: right;
			}

			.total-row td {
				font-weight: 600;
			}

			.grand-total-row td {
				color: ${CLIENT_DOCUMENT_BODY_COLOR};
				font-weight: 700;
			}

			.schedule-phase {
				margin: 0 0 20px;
			}

			.quotation-document section p {
				margin: 0 0 9px;
			}

			.schedule-phase:last-child {
				margin-bottom: 0;
			}

			.terms {
				margin: 16px 0 0 !important;
				font-style: italic;
				line-height: 1.45;
			}

			.terms strong {
				font-weight: 700;
			}

			.quotation-payment-terms {
				margin-top: 28px;
			}

			.quotation-payment-terms h2 {
				margin-bottom: 9px;
			}

			.quotation-payment-terms p {
				margin: 0;
			}

			.validity {
				margin: 16px 0 0 !important;
			}

			.quotation-page-footer {
				position: absolute;
				left: 72px;
				right: 72px;
				bottom: 34px;
				padding-top: 10px;
				border-top: 1px solid #e5e5e5;
				font-size: 10px;
				color: #555;
			}

			.quotation-page-number {
				display: block;
				margin-top: 4px;
				color: #777;
				font-size: 9px;
				text-align: right;
			}

			/* From page 2: the document's name above a thin line, as on the director's Word documents.
			   The section below brings its own top margin. */
			.quotation-page-header {
				padding-bottom: 5px;
				border-bottom: 1px solid #e5e5e5;
				font-size: 10px;
				line-height: 1.25;
				color: #555;
				text-align: right;
			}

			@media print {
				.quotation-pdf-root {
					padding: 0;
					background: #fff;
				}

				.quotation-document {
					margin: 0;
					box-shadow: none;
				}
			}
		</style>
	`;
};
